import React, { useEffect, useMemo, useState } from 'react';
import classes from './PromoCodesList.module.css';
import IconButton from '../../UI/IconButton/IconButton';
import EditIcon from '../../UI/icons/EditIcon/EditIcon';
import TrashIcon from '../../UI/icons/TrashIcon/TrashIcon';
import Chip from '../../UI/Chip/Chip';
import { PromoCode } from '../../../types/common';
import Button from '../../UI/Button/Button';

interface Props {
    items: PromoCode[];
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}

const collator = new Intl.Collator('ru', {
    usage: 'sort',
    sensitivity: 'base',
    numeric: true,
    ignorePunctuation: true,
});

const DEFAULT_VISIBLE_COUNT = 4;

const PromoCodesList: React.FC<Props> = ({ items, onEdit, onDelete }) => {
    const [showAll, setShowAll] = useState(false);

    const sortedEntries = useMemo(() => {
        const withIndex = items.map((item, index) => ({ item, index }));
        withIndex.sort((a, b) => {
            const aCodeRaw = (a.item.code ?? '').trim();
            const bCodeRaw = (b.item.code ?? '').trim();
            const aEmpty = aCodeRaw.length === 0;
            const bEmpty = bCodeRaw.length === 0;
            if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
            const byCode = collator.compare(aCodeRaw, bCodeRaw);
            if (byCode !== 0) return byCode;
            const aType = (a.item.type ?? '').trim();
            const bType = (b.item.type ?? '').trim();
            const byType = collator.compare(aType, bType);
            if (byType !== 0) return byType;
            const aStatus = (a.item.status ?? '').trim();
            const bStatus = (b.item.status ?? '').trim();
            const byStatus = collator.compare(aStatus, bStatus);
            if (byStatus !== 0) return byStatus;
            const byId = collator.compare(a.item.id, b.item.id);
            if (byId !== 0) return byId;
            return a.index - b.index;
        });
        return withIndex;
    }, [items]);

    const shouldCollapse = sortedEntries.length > DEFAULT_VISIBLE_COUNT;

    useEffect(() => {
        if (!shouldCollapse && showAll) setShowAll(false);
    }, [shouldCollapse, showAll]);

    const visibleEntries = useMemo(() => {
        if (!shouldCollapse) return sortedEntries;
        return showAll ? sortedEntries : sortedEntries.slice(0, DEFAULT_VISIBLE_COUNT);
    }, [sortedEntries, shouldCollapse, showAll]);

    return (
        <div className={classes.wrapper}>
            <ul className={classes.list}>
                {visibleEntries.map(({ item, index }) => {
                    const { id, code, discountType, discountValue, usageLimit, usageCount, status, type, phone } = item;
                    return (
                        <li key={`promo-elem-${index}-${id}`} className={classes.item}>
                            <div className={classes.content}>
                                <span className={`${classes.code} ${type === 'personal' ? classes.personalCode : ''}`}>{code}</span>
                                <span className={classes.discount}>{discountType === 'percentage' ? `${discountValue}%` : `${discountValue} балл.`}</span>
                                <span className={classes.type}>{type === 'global' ? 'Общий' : 'Персональный'}</span>
                                <span className={`${classes.status} ${status === 'inactive' ? classes.inactive : ''}`}>
                                    {status === 'active' ? 'Активен' : 'Неактивен'}
                                </span>
                                <div className={classes.actions}>
                                    <IconButton onClick={() => onEdit(id)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => onDelete(id)}>
                                        <TrashIcon fill={'lightgray'} />
                                    </IconButton>
                                </div>
                            </div>
                            <div className={classes.chips}>
                                {type === 'global' ? <Chip text={`Использовано: ${usageCount}/${usageLimit}`} /> : phone && <Chip text={phone} />}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {shouldCollapse && !showAll && (
                <div className={classes.footer}>
                    <Button mode="secondary" onClick={() => setShowAll(true)} title="Показать все промо-коды">
                        Показать все
                    </Button>
                </div>
            )}
        </div>
    );
};

export default PromoCodesList;
